/**
 * <s-read-more> — Folds long content to a set height with a fade and a Load more / Show less toggle.
 *
 * Markup:
 *   [data-read-more-body]    — content wrapper; its max-height is set while folded.
 *   [data-read-more-toggle]  — button (starts hidden); shown only when the content is taller than the fold.
 *   [data-read-more-label]   — text inside the button.
 *   data-more-label / data-less-label on the host — translated button labels.
 *   data-fold (optional)     — fold height in px. Default 256.
 * Sets [data-collapsed] or [data-expanded] on the host for styling.
 */
class SReadMore extends HTMLElement {
  #controller = null;
  #resize = null;
  #expanded = false;

  connectedCallback() {
    const body = this.querySelector('[data-read-more-body]');
    const toggle = this.querySelector('[data-read-more-toggle]');
    if (!body || !toggle) return;
    this.#controller = new AbortController();
    const fold = Number(this.dataset.fold) || 256;
    // Only fold when it saves real space (more than ~40% extra), so a few extra lines never hide behind a button.
    const measure = () => {
      const needed = body.scrollHeight > fold * 1.4;
      toggle.hidden = !needed;
      if (!needed) {
        body.style.maxHeight = '';
        this.removeAttribute('data-collapsed');
        this.removeAttribute('data-expanded');
        return;
      }
      body.style.maxHeight = this.#expanded ? `${body.scrollHeight}px` : `${fold}px`;
      this.toggleAttribute('data-collapsed', !this.#expanded);
      this.toggleAttribute('data-expanded', this.#expanded);
    };

    toggle.addEventListener('click', () => {
      const wasExpanded = this.#expanded;
      this.#expanded = !this.#expanded;
      toggle.setAttribute('aria-expanded', String(this.#expanded));
      const label = toggle.querySelector('[data-read-more-label]') ?? toggle;
      label.textContent = this.#expanded ? this.dataset.lessLabel : this.dataset.moreLabel;
      measure();
      // Folding back up: keep the description in view instead of leaving the shopper far down the page.
      if (wasExpanded) {
        const top = this.getBoundingClientRect().top;
        if (top < 0) this.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }
    }, { signal: this.#controller.signal });

    this.#resize = new ResizeObserver(() => measure());
    this.#resize.observe(body.firstElementChild ?? body);
    measure();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#resize?.disconnect();
  }
}

if (!customElements.get('s-read-more')) customElements.define('s-read-more', SReadMore);
